// Web Crypto helpers: PBKDF2 + AES-GCM for encrypting R2 credentials with admin password.
const enc = new TextEncoder();
const dec = new TextDecoder();

const PBKDF2_ITERATIONS = 250000;

function b64encode(buf) {
  return btoa(String.fromCharCode(...new Uint8Array(buf)));
}
function b64decode(str) {
  return Uint8Array.from(atob(str), (c) => c.charCodeAt(0));
}

export async function deriveKey(password, saltB64) {
  const salt = b64decode(saltB64);
  const baseKey = await crypto.subtle.importKey(
    "raw",
    enc.encode(password),
    "PBKDF2",
    false,
    ["deriveKey"]
  );
  return crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt,
      iterations: PBKDF2_ITERATIONS,
      hash: "SHA-256",
    },
    baseKey,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
}

export function randomSaltB64(bytes = 16) {
  const buf = new Uint8Array(bytes);
  crypto.getRandomValues(buf);
  return b64encode(buf);
}

export async function encryptJSON(obj, password, saltB64) {
  const salt = saltB64 || randomSaltB64();
  const key = await deriveKey(password, salt);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const plaintext = enc.encode(JSON.stringify(obj));
  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    plaintext
  );
  return {
    salt,
    iv: b64encode(iv),
    ciphertext: b64encode(ciphertext),
    v: 1,
  };
}

export async function decryptJSON(blob, password) {
  const key = await deriveKey(password, blob.salt);
  const plainBuf = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: b64decode(blob.iv) },
    key,
    b64decode(blob.ciphertext)
  );
  return JSON.parse(dec.decode(plainBuf));
}

// Password hash (SHA-256) for verification even without trying decrypt.
export async function sha256Hex(str) {
  const buf = await crypto.subtle.digest("SHA-256", enc.encode(str));
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}
